/**
 * token-popup.js - after a card that makes tokens is added to the cart, offer those tokens.
 *
 * Data: token-index.json on Shopify Files (index-builder/build-token-index.mjs, hourly):
 *   { t: { tokenHandle: [variantId, shortName, key] }, c: { cardHandle: [tokenHandle, ...] } }
 * Trigger: reco-tracker.js dispatches `mtgreco:cart-changed` with
 *   detail { kind: 'add', handles, source } for every /cart/add on any page; adds sent by this
 *   file carry X-Mtg-Source: token-offer and are ignored.
 * One candidate -> corner card (hides after HIDE_MS, paused on hover/focus); two or more -> checklist dialog.
 * Declined tokens are not offered again in this visit (sessionStorage). Never on /cart.
 * Everything is wrapped in try/catch: this must never break the page or the add itself.
 */
(function () {
  'use strict';

  var MAX_ITEMS = 40; // safety cap only
  var VISIBLE_ROWS = 5;
  var HIDE_MS = 10000;
  var SOURCE = 'token-offer';
  var DECLINED_KEY = 'mtgkarte_token_declined';
  var INDEX_KEY = 'mtgkarte_token_index';
  var INDEX_TTL_MS = 3600 * 1000;

  var LABELS = {
    en: {
      need: 'Need the token?', add: 'Add to cart', added: 'Added ✓', noThanks: 'No thanks', soldOut: 'Out of stock', close: 'Close',
      makeN: function (n) { return 'Your cards make ' + n + ' tokens'; },
      showAll: function (n) { return 'Show all ' + n + ' tokens ▾'; }, showFewer: 'Show fewer ▴',
      addSelected: function (n, total) { return 'Add ' + n + (n === 1 ? ' token' : ' tokens') + ' (' + total + ')'; }
    },
    sr: {
      need: 'Treba ti i token?', add: 'Dodaj u korpu', added: 'Dodato ✓', noThanks: 'Ne, hvala', soldOut: 'Nema na stanju', close: 'Zatvori',
      makeN: function (n) { return 'Tvoje karte prave ' + n + ' tokena'; },
      showAll: function (n) { return 'Prikaži sve (' + n + ') ▾'; }, showFewer: 'Prikaži manje ▴',
      addSelected: function (n, total) { return 'Dodaj ' + n + (n === 1 ? ' token' : ' tokena') + ' (' + total + ')'; }
    }
  };

  function labels(locale) {
    var l = String(locale || '').slice(0, 2).toLowerCase();
    return LABELS[l] || LABELS.en;
  }

  // Matching key of an index row: the small int (same oracle = same token), or the handle for old 2-element rows.
  function keyOf(handle, row) {
    return row && row.length > 2 && row[2] != null ? 'k' + row[2] : 'h' + handle;
  }

  function candidatesFor(index, added, inCart, declined) {
    var out = [];
    try {
      if (!index || !index.c || !index.t) return out;
      var addedSet = {};
      (added || []).forEach(function (h) { addedSet[h] = true; });
      var seen = {}, seenKey = {};
      // keys already in the cart (a Treasure from another set counts)
      inCart.forEach(function (h) { var r = index.t[h]; if (r) seenKey[keyOf(h, r)] = true; });
      (added || []).forEach(function (card) {
        (index.c[card] || []).forEach(function (tok) {
          if (seen[tok] || addedSet[tok] || inCart.has(tok) || declined.has(tok)) return;
          var row = index.t[tok];
          if (!row) return;
          var key = keyOf(tok, row);
          if (seenKey[key]) return;
          seen[tok] = true;
          seenKey[key] = true;
          out.push({ token: tok, variantId: row[0], name: row[1], key: key });
        });
      });
    } catch (e) {
      return [];
    }
    return out.slice(0, MAX_ITEMS);
  }

  function pickVariant(product, preferredId) {
    try {
      var vs = (product && product.variants) || [];
      var pref = null, first = null;
      vs.forEach(function (v) {
        if (!v || !v.available) return;
        if (!first) first = v;
        if (Number(v.id) === Number(preferredId)) pref = v;
      });
      var v = pref || first;
      return v ? { id: Number(v.id), price: Number(v.price) } : null;
    } catch (e) {
      return null;
    }
  }

  // Same as reco-cart.js formatPrice: shop money format "60,00 RSD" regardless of visitor locale.
  function formatPrice(minorUnits, currency) {
    var amount = (Number(minorUnits) || 0) / 100;
    try {
      return new Intl.NumberFormat('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' ' + currency;
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function session() {
    try { return window.sessionStorage || null; } catch (e) { return null; }
  }

  function readDeclined() {
    try {
      var s = session();
      var arr = s ? JSON.parse(s.getItem(DECLINED_KEY) || '[]') : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch (e) {
      return new Set();
    }
  }

  function writeDeclined(handles) {
    try {
      var s = session();
      if (!s) return;
      var cur = readDeclined();
      (handles || []).forEach(function (h) { cur.add(h); });
      s.setItem(DECLINED_KEY, JSON.stringify(Array.from(cur)));
    } catch (e) {
      // no-op
    }
  }

  // Append new candidates to the ones on screen: dedupe by token (also inside the new list), cap at max.
  function mergeShown(shown, list, max) {
    var out = [], seen = {}, seenKey = {};
    try {
      (shown || []).concat(list || []).forEach(function (c) {
        if (!c || seen[c.token] || (c.key && seenKey[c.key])) return;
        seen[c.token] = true;
        if (c.key) seenKey[c.key] = true;
        out.push(c);
      });
    } catch (e) { return []; }
    return out.slice(0, max);
  }

  var W = typeof window !== 'undefined' ? window : globalThis;
  W.MtgTokenOffer = {
    candidatesFor: candidatesFor, pickVariant: pickVariant, formatPrice: formatPrice, labels: labels,
    readDeclined: readDeclined, writeDeclined: writeDeclined, mergeShown: mergeShown,
    MAX_ITEMS: MAX_ITEMS, VISIBLE_ROWS: VISIBLE_ROWS, HIDE_MS: HIDE_MS, SOURCE: SOURCE
  };

  // Loaded in Node by the tests: expose the pure parts only.
  if (typeof document === 'undefined') return;

  var SCRIPT = document.currentScript;
  var INDEX_URL = SCRIPT && SCRIPT.getAttribute('data-token-index');
  var CSS_URL = SCRIPT && SCRIPT.getAttribute('data-token-css');
  var indexPromise = null;
  var cssPromise = null;
  var root = null;     // <token-offer> while shown
  var shown = [];      // candidates on screen: { token, variantId, name, product }
  var hideTimer = null;
  var expanded = false; // list mode: hidden rows revealed

  function L() { return labels((window.Shopify && window.Shopify.locale) || 'en'); }
  function currency() {
    try { return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'RSD'; } catch (e) { return 'RSD'; }
  }
  function onCartPage() { return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/i.test(location.pathname); }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    indexPromise = new Promise(function (resolve) {
      try {
        var s = session();
        var c = s && JSON.parse(s.getItem(INDEX_KEY) || 'null');
        if (c && c.data && Date.now() - c.at < INDEX_TTL_MS) return resolve(c.data);
      } catch (e) { /* fall through */ }
      if (!INDEX_URL) return resolve(null);
      fetch(INDEX_URL, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !data.c) { indexPromise = null; return resolve(null); }
          try { var s2 = session(); if (s2) s2.setItem(INDEX_KEY, JSON.stringify({ at: Date.now(), data: data })); } catch (e) { /* quota */ }
          resolve(data);
        })
        .catch(function () { indexPromise = null; resolve(null); });
    });
    return indexPromise;
  }

  function cartHandles() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var set = new Set();
        ((cart && cart.items) || []).forEach(function (i) { if (i && i.handle) set.add(i.handle); });
        return set;
      })
      .catch(function () { return new Set(); });
  }

  function withProduct(c) {
    return fetch('/products/' + encodeURIComponent(c.token) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var v = pickVariant(p, c.variantId);
        if (!v) return null;
        var img = (p && (p.featured_image || (p.images && p.images[0]))) || '';
        if (img && img.indexOf('//') === 0) img = 'https:' + img;
        return { token: c.token, variantId: v.id, price: v.price, name: c.name, key: c.key, image: img };
      })
      .catch(function () { return null; });
  }

  // Inject the stylesheet once, on first use; resolves when loaded (or on error / after 1500 ms).
  function ensureCss() {
    if (cssPromise) return cssPromise;
    cssPromise = new Promise(function (resolve) {
      try {
        if (!CSS_URL) return resolve();
        var link = document.querySelector('link[data-token-popup-css]');
        if (link) return resolve();
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = CSS_URL;
        link.setAttribute('data-token-popup-css', '');
        var done = false;
        var finish = function () { if (!done) { done = true; resolve(); } };
        link.addEventListener('load', finish);
        link.addEventListener('error', finish);
        setTimeout(finish, 1500);
        document.head.appendChild(link);
      } catch (e) { resolve(); }
    });
    return cssPromise;
  }

  function onAdd(detail) {
    if (!detail || detail.kind !== 'add' || detail.source === SOURCE || onCartPage()) return;
    var added = detail.handles || [];
    if (!added.length) return;
    Promise.all([loadIndex(), cartHandles()]).then(function (r) {
      var index = r[0], inCart = r[1];
      var already = new Set(shown.map(function (s) { return s.token; }));
      var cands = candidatesFor(index, added, inCart, readDeclined()).filter(function (c) { return !already.has(c.token); });
      if (!cands.length) return;
      return Promise.all(cands.map(withProduct)).then(function (list) {
        list = list.filter(Boolean);
        if (!list.length) return;
        shown = mergeShown(shown, list, MAX_ITEMS);
        return ensureCss().then(function () { if (shown.length) render(); });
      });
    }).catch(function () { /* silent */ });
  }

  function sized(url, w) {
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function close(declineRest, fromTimer) {
    try {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      if (declineRest) writeDeclined(shown.map(function (s) { return s.token; }));
      // Give focus back only if the popup actually held it; never after the auto-hide timer.
      var hadFocus = !fromTimer && root && root.contains(document.activeElement);
      if (root && root.parentNode) root.parentNode.removeChild(root);
      if (hadFocus && root.__returnFocus && root.__returnFocus.focus) {
        try { root.__returnFocus.focus({ preventScroll: true }); } catch (e2) { root.__returnFocus.focus(); }
      }
    } catch (e) { /* no-op */ }
    root = null;
    shown = [];
    expanded = false;
  }

  function armTimer() {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () { close(true, true); }, HIDE_MS);
  }

  function render() {
    var l = L(), cur = currency();
    var wasOpen = !!root;
    var returnFocus = wasOpen ? root.__returnFocus : document.activeElement;
    var hadFocus = wasOpen && root.contains(document.activeElement);
    var wasCorner = wasOpen && root.className.indexOf('token-offer--corner') >= 0;
    var ticks = {};
    if (wasOpen) {
      Array.prototype.forEach.call(root.querySelectorAll('input[type=checkbox]'), function (c) {
        ticks[c.getAttribute('data-token')] = c.checked;
      });
    }
    if (root && root.parentNode) root.parentNode.removeChild(root);
    root = document.createElement('token-offer');
    root.__returnFocus = returnFocus;

    if (shown.length === 1) {
      var s = shown[0];
      root.className = 'token-offer token-offer--corner';
      root.setAttribute('role', 'status');
      root.innerHTML =
        '<button type="button" class="token-offer__x" aria-label="' + esc(l.close) + '">×</button>' +
        (s.image ? '<img class="token-offer__img" src="' + esc(sized(s.image, 160)) + '" alt="" width="72" loading="lazy">' : '') +
        '<div class="token-offer__body">' +
          '<div class="token-offer__kicker">' + esc(l.need) + '</div>' +
          '<div class="token-offer__name">' + esc(s.name) + '</div>' +
          '<div class="token-offer__price">' + esc(formatPrice(s.price, cur)) + '</div>' +
          '<button type="button" class="token-offer__add">' + esc(l.add) + '</button>' +
        '</div>';
      root.addEventListener('mouseenter', function () { if (hideTimer) clearTimeout(hideTimer); });
      root.addEventListener('focusin', function () { if (hideTimer) clearTimeout(hideTimer); });
      root.addEventListener('touchstart', function () { if (hideTimer) clearTimeout(hideTimer); }, { passive: true });
      root.addEventListener('mouseleave', armTimer);
      root.querySelector('.token-offer__add').addEventListener('click', function (ev) { addTokens([s], ev.currentTarget); });
      armTimer();
    } else {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      root.className = 'token-offer token-offer--list';
      var rowHtml = shown.map(function (s, i) {
        return '<label class="token-offer__row">' +
          '<input type="checkbox"' + (ticks[s.token] === false ? '' : ' checked') + ' data-i="' + i + '" data-token="' + esc(s.token) + '">' +
          (s.image ? '<img src="' + esc(sized(s.image, 80)) + '" alt="" width="36" loading="lazy">' : '<span class="token-offer__noimg"></span>') +
          '<span class="token-offer__row-name">' + esc(s.name) + '</span>' +
          '<span class="token-offer__price">' + esc(formatPrice(s.price, cur)) + '</span>' +
        '</label>';
      });
      var more = rowHtml.length > VISIBLE_ROWS;
      var rows = rowHtml.slice(0, VISIBLE_ROWS).join('') +
        (more ? '<div class="token-offer__extra"' + (expanded ? '' : ' hidden') + '>' + rowHtml.slice(VISIBLE_ROWS).join('') + '</div>' : '');
      root.innerHTML =
        '<div class="token-offer__backdrop"></div>' +
        '<div class="token-offer__dialog" role="dialog" aria-modal="true" aria-labelledby="token-offer-title">' +
          '<button type="button" class="token-offer__x" aria-label="' + esc(l.close) + '">×</button>' +
          '<div class="token-offer__title" id="token-offer-title">' + esc(l.makeN(shown.length)) + '</div>' +
          '<div class="token-offer__rows">' + rows + '</div>' +
          (more ? '<button type="button" class="token-offer__more" aria-expanded="' + (expanded ? 'true' : 'false') + '">' +
            esc(expanded ? l.showFewer : l.showAll(shown.length)) + '</button>' : '') +
          '<button type="button" class="token-offer__add"></button>' +
          '<button type="button" class="token-offer__no">' + esc(l.noThanks) + '</button>' +
        '</div>';
      var addBtn = root.querySelector('.token-offer__add');
      var refresh = function () {
        var sel = selected();
        var total = sel.reduce(function (a, s) { return a + s.price; }, 0);
        addBtn.textContent = l.addSelected(sel.length, formatPrice(total, cur));
        addBtn.disabled = !sel.length;
      };
      var selected = function () {
        return Array.prototype.filter.call(root.querySelectorAll('input[type=checkbox]'), function (c) { return c.checked; })
          .map(function (c) { return shown[Number(c.getAttribute('data-i'))]; })
          .filter(Boolean); // shown is emptied right after an add while the dialog is still up
      };
      var moreBtn = root.querySelector('.token-offer__more');
      if (moreBtn) {
        moreBtn.addEventListener('click', function () {
          expanded = !expanded;
          var extra = root.querySelector('.token-offer__extra');
          if (extra) { if (expanded) extra.removeAttribute('hidden'); else extra.setAttribute('hidden', ''); }
          moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          moreBtn.textContent = expanded ? l.showFewer : l.showAll(shown.length);
        });
      }
      root.addEventListener('change', refresh);
      addBtn.addEventListener('click', function (ev) { addTokens(selected(), ev.currentTarget); });
      root.querySelector('.token-offer__no').addEventListener('click', function () { close(true); });
      root.querySelector('.token-offer__backdrop').addEventListener('click', function () { close(true); });
      refresh();
    }
    root.querySelector('.token-offer__x').addEventListener('click', function () { close(true); });
    document.body.appendChild(root);
    if (shown.length > 1 && (!wasOpen || hadFocus || wasCorner)) { var f = root.querySelector('.token-offer__add'); if (f) f.focus(); }
  }

  function refreshBubble() {
    return fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart?section_id=cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var el = document.getElementById('cart-icon-bubble');
        if (!el) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var wrapped = doc.querySelector('.shopify-section');
        el.innerHTML = wrapped ? wrapped.innerHTML : doc.body.innerHTML;
      })
      .catch(function () { /* no-op */ });
  }

  function addTokens(list, button) {
    if (!list.length) return;
    var l = L();
    button.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Mtg-Source': SOURCE },
      body: JSON.stringify({ items: list.map(function (s) { return { id: s.variantId, quantity: 1 }; }) })
    })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw d; return d; }); })
      .then(function () {
        button.textContent = l.added;
        var addedTokens = list.map(function (s) { return s.token; });
        writeDeclined(shown.map(function (s) { return s.token; }).filter(function (t) { return addedTokens.indexOf(t) === -1; }));
        // Every shown token is now added or declined: a quick next add must not re-offer them.
        shown = [];
        refreshBubble();
        // An open cart drawer (theme add-to-cart opens it) would still list the old items.
        try {
          var drawer = document.querySelector('cart-drawer-items');
          if (drawer && typeof drawer.onCartUpdate === 'function') drawer.onCartUpdate();
        } catch (e) { /* no-op */ }
        var r1 = root;
        setTimeout(function () { if (root === r1) close(false); }, 1200);
      })
      .catch(function () {
        button.textContent = l.soldOut;
        writeDeclined(shown.map(function (s) { return s.token; }));
        shown = [];
        var r2 = root;
        setTimeout(function () { if (root === r2) close(false); }, 1800);
      });
  }

  document.addEventListener('keydown', function (ev) {
    try {
      if (root && (ev.key === 'Escape' || ev.key === 'Esc')) {
        if (root.contains(document.activeElement)) ev.stopPropagation();
        close(true);
      }
    } catch (e) { /* no-op */ }
  });

  document.addEventListener('mtgreco:cart-changed', function (ev) {
    try { onAdd(ev && ev.detail); } catch (e) { /* no-op */ }
  });
})();
